import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { sentenceCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  Alert,
  Drawer,
  Button,
  Stack,
  Box,
  Tooltip,
  IconButton,
  Divider,
  Typography,
  Menu,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Iconify from '../../../components/iconify';
import FormProvider, {
  RHFSelect,
  RHFTextField,
} from '../../../components/hook-form';
import Scrollbar from '../../../components/scrollbar';
import { BOOKING_STATUS_COLORS } from '../../../theme/palette';
import Label from '../../../components/label';
import {
  getNow,
  endOfMonth,
  formatBookingDateForm,
  formatBookingTime,
  formatSlots,
} from '../../../utils/formatTime';
import axios from '../../../utils/Axios';
import { useAuthContext } from 'src/auth/useAuthContext';
import { getSymbol } from 'src/pages/DashboardAppPage';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/unsaved-changes-dialog';
import NotificationHistory from './NotificationHistory';

const CALENDAR_LOAD_ERROR =
  'Could not load available dates. Open the calendar again to retry.';
const CREATE_ERROR = 'Failed to create booking. Please try again.';

const StyledLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 2.5,
}));

export default function BookingModal({
  booking,
  openModal,
  onCloseModal,
  services,
  customers,
  isNew,
  createBooking,
  timezone,
  onUpdateStatus,
}) {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [serviceId, setServiceId] = useState(null);
  const [cal, setCal] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [calendarError, setCalendarError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // The service and month that `cal` holds, or that a request is loading.
  // It lives in the component and is cleared with `cal`, so it can never
  // claim dates that are not loaded.
  const loadedCalendarRef = useRef('');
  const defaultValues = useMemo(
    () => ({
      customer: booking?.customer?._id || '',
      service: booking?.service?._id || '',
      startDate: booking?.startDate || '',
      internalNote: booking?.internalNote || '',
      additionalData: booking?.additionalData || '',
      price: booking?.price || '',
      paymentReceived: "false",
    }),
    [booking]
  );

  const methods = useForm({ defaultValues });
  const { reset, handleSubmit, formState: { isSubmitting, isDirty, isSubmitSuccessful } } = methods;
  const [selectedDate, setSelectedDate] = useState(getNow());
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({ open: false, data: null });

  // Get full customer details from the customers prop
  const customerDetails = useMemo(() => {
    if (!booking?.customer?._id || !customers?.length) return null;
    return customers.find(c => c._id === booking.customer._id) || null;
  }, [booking, customers]);

  // Unsaved changes hook (only for new bookings since viewing existing ones shouldn't have unsaved changes)
  const { handleClose } = useUnsavedChanges({
    formState: { isDirty, isSubmitSuccessful },
    onUnsavedChanges: isNew ? (data) => {
      setUnsavedChangesDialog({ open: true, data });
    } : null,
    isOpen: openModal
  });

  const clearCalendar = useCallback(() => {
    loadedCalendarRef.current = '';
    setCal(null);
    setCalendarError(null);
  }, []);

  const loadCalendar = useCallback(async (service, date) => {
    const month = date.startOf('month');
    const startDate = month.format('YYYY-MM-DD');
    const key = `${service}|${startDate}`;
    if (loadedCalendarRef.current === key) return;

    loadedCalendarRef.current = key;
    setCalendarError(null);
    const endDate = `${month.get('year')}-${month.get('month') + 1}-${endOfMonth(
      month
    )}`;

    try {
      const calendar = await axios.get(
        `/services/${service}/calendar?startDate=${startDate}&endDate=${endDate}`
      );
      // Drop the answer if the admin has moved to another service or month.
      if (loadedCalendarRef.current !== key) return;
      setCal(calendar.data);
    } catch (error) {
      if (loadedCalendarRef.current !== key) return;
      console.error('Failed to load available dates:', error);
      loadedCalendarRef.current = '';
      setCal(null);
      setCalendarError(CALENDAR_LOAD_ERROR);
    }
  }, []);

  const onCalendarOpen = useCallback(() => {
    const service = methods.getValues('service');
    if (!service || !selectedDate) return;
    loadCalendar(service, selectedDate);
  }, [methods, loadCalendar, selectedDate]);

  const onMonthChange = useCallback(
    (date) => {
      const service = methods.getValues('service');
      if (!service) return;
      loadCalendar(service, date);
    },
    [methods, loadCalendar]
  );

  const shouldDisableDate = useCallback(
    (date) => {
      if (!cal) return true;
      const formatedDate = date.format('YYYY-MM-DD');
      return cal[formatedDate] ? !cal[formatedDate].length : true;
    },
    [cal]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [booking, !isNew]);

  // Every opening loads fresh dates: the last booking may have used a slot.
  useEffect(() => {
    if (!openModal) return;
    clearCalendar();
    setSubmitError(null);
  }, [openModal, clearCalendar]);

  const onSubmit = async (data) => {
    setSubmitError(null);
    try {
      await createBooking(data);
      // Close modal directly after successful save without unsaved changes check
      onCloseModal();
      clearCalendar();
      setUnsavedChangesDialog({ open: false, data: null });
      reset();
    } catch (error) {
      console.error('Booking creation failed:', error);
      // The page alert sits behind the drawer, so show the reason in here.
      setSubmitError(error?.messages?.[0] || CREATE_ERROR);
    }
  };

  const maybeCurrentTimeSlots = () => {
    if (!cal || !selectedDate) return [];
    return cal[selectedDate.format('YYYY-MM-DD')] || [];
  };

  // Handle modal close with unsaved changes check
  const handleCloseModal = () => {
    if (isNew) {
      handleClose(() => {
        onCloseModal();
        setUnsavedChangesDialog({ open: false, data: null });
      });
    } else {
      onCloseModal();
    }
  };

  // Handle unsaved changes dialog actions
  const handleUnsavedChangesDiscard = () => {
    if (unsavedChangesDialog.data?.onDiscard) {
      unsavedChangesDialog.data.onDiscard();
    }
    setUnsavedChangesDialog({ open: false, data: null });
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ open: false, data: null });
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={openModal}
      onClose={handleCloseModal}
      PaperProps={{
        sx: {
          width: {
            xs: 1,
            sm: 480,
          },
        },
      }}
    >
      <ToolBar>
        {!isNew && booking?.status ? (
          booking.status === 'NO_SHOW' ? (
            <Label
              sx={{
                ml: 2,
                height: 'auto',
                color: BOOKING_STATUS_COLORS[booking.status]?.color,
                background: BOOKING_STATUS_COLORS[booking.status]?.background,
                textAlign: 'center',
              }}
            >
              {sentenceCase(booking.status)}
            </Label>
          ) : (
            <>
              <Label
                sx={{
                  ml: 2,
                  height: 'auto',
                  color: BOOKING_STATUS_COLORS[booking.status]?.color,
                  background: BOOKING_STATUS_COLORS[booking.status]?.background,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
                onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
              >
                {sentenceCase(booking.status)}
                <Iconify icon="eva:chevron-down-fill" width={16} height={16} sx={{ ml: 0.5 }} />
              </Label>
              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={() => setStatusMenuAnchor(null)}
                PaperProps={{
                  sx: { p: 1, width: 140, '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 } },
                }}
              >
                {booking.status !== 'APPROVED' && (
                  <MenuItem sx={{ color: 'success.main' }} onClick={() => { onUpdateStatus('APPROVED'); setStatusMenuAnchor(null); }}>
                    Approve
                  </MenuItem>
                )}
                {booking.status !== 'PENDING' && (
                  <MenuItem sx={{ color: 'warning.main' }} onClick={() => { onUpdateStatus('PENDING'); setStatusMenuAnchor(null); }}>
                    Pending
                  </MenuItem>
                )}
                {booking.status !== 'REJECTED' && (
                  <MenuItem sx={{ color: 'error.dark' }} onClick={() => { onUpdateStatus('REJECTED'); setStatusMenuAnchor(null); }}>
                    Reject
                  </MenuItem>
                )}
                {booking.status !== 'CANCELED' && (
                  <MenuItem sx={{ color: 'error.main' }} onClick={() => { onUpdateStatus('CANCELED'); setStatusMenuAnchor(null); }}>
                    Cancel
                  </MenuItem>
                )}
                {booking.status !== 'COMPLETED' && (
                  <MenuItem sx={{ color: 'success.dark' }} onClick={() => { onUpdateStatus('COMPLETED'); setStatusMenuAnchor(null); }}>
                    Completed
                  </MenuItem>
                )}
              </Menu>
            </>
          )
        ) : (
          ''
        )}
        <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
          <Tooltip title="Close">
            <IconButton onClick={handleCloseModal}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Tooltip>
        </Stack>
      </ToolBar>
      <Divider />
      <Stack sx={{ p: 2 }}>
        <Typography variant="h5">{isNew ? 'New' : 'View'} Booking</Typography>
      </Stack>
      <Scrollbar>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack sx={{ p: 2 }}>
            {isNew ? (
              /* ===== NEW BOOKING FORM ===== */
              <>
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                >
                  <Stack>
                    <StyledLabel>Service:</StyledLabel>
                    <RHFSelect
                    native
                    name="service"
                    size="small"
                    rules={{ required: 'Service is required' }}
                    onChange={(e) => {
                      setServiceId(e.target.value)
                      methods.setValue('service', e.target.value)
                      // Slots belong to one service, so drop them and the chosen time.
                      methods.setValue('startDate', '')
                      clearCalendar()
                      }} >
                      <option value="">Select Service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service._id}>
                          {service.name}
                        </option>
                      ))}
                    </RHFSelect>
                  </Stack>
                  <Stack>
                    <StyledLabel>Customer Name:</StyledLabel>
                    <RHFSelect
                      native
                      name="customer"
                      size="small"
                      rules={{ required: 'Customer is required' }}
                    >
                      <option value="">Select Customer</option>
                      {customers.map((customer, index) => (
                        <option key={index} value={customer._id}>
                          {customer.firstName} {customer.lastName}
                        </option>
                      ))}
                    </RHFSelect>
                  </Stack>
                  <Stack>
                    <StyledLabel>Date:</StyledLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        rules={{ required: 'Date is required' }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            helperText:
                              'Click on the calendar icon to pick a date',
                          },
                        }}
                        onOpen={onCalendarOpen}
                        onMonthChange={onMonthChange}
                        views={['month', 'day']}
                        shouldDisableDate={shouldDisableDate}
                        onChange={(newDate) => setSelectedDate(newDate)}
                      />
                    </LocalizationProvider>
                  </Stack>
                  <Stack>
                    <StyledLabel>Time:</StyledLabel>
                    <RHFSelect
                      native
                      name="startDate"
                      size="small"
                      disabled={!cal}
                      rules={{ required: 'Time is required' }}
                    >
                      <option value="">Select Time:</option>
                      {maybeCurrentTimeSlots().map((time, index) => (
                        <option key={index} value={time}>
                          {formatSlots(time)}
                        </option>
                      ))}
                    </RHFSelect>
                  </Stack>
                </Box>
                {calendarError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {calendarError}
                  </Alert>
                )}
                {user.config.enablePayments ? <Stack sx={{ mt: 3, mb: 1 }}>
                  <StyledLabel>Service Price: {services.find(service => service._id == serviceId)?.price ? `${getSymbol(user.config.currency)} ${services.find(service => service._id == serviceId).price}` : 'FREE'}</StyledLabel>
                  {services.find(service => service._id == serviceId)?.price ? <>
                    <StyledLabel>Payment Received</StyledLabel>
                    <RHFSelect
                      native
                      name="paymentReceived"
                      size="small"
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </RHFSelect>
                  </> : <></>}
                </Stack> : ''}
                <Stack sx={{ mt: 3, mb: 1 }}>
                  <StyledLabel>Additional Data:</StyledLabel>
                  <RHFTextField
                    multiline
                    name="additionalData"
                    rows={4}
                    size="small"
                  />
                </Stack>
              </>
            ) : (
              /* ===== VIEW BOOKING DETAILS ===== */
              <>
                {/* Booking Details Section */}
                <Box
                  rowGap={2}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                >
                  <DetailItem
                    icon="eva:briefcase-outline"
                    label="Service"
                    value={booking?.service?.name || '—'}
                  />
                  <DetailItem
                    icon="eva:calendar-outline"
                    label="Date"
                    value={booking?.startDate ? formatBookingDateForm(booking.startDate, timezone) : '—'}
                  />
                  <DetailItem
                    icon="eva:clock-outline"
                    label="Time"
                    value={booking?.startDate ? formatBookingTime(booking.startDate, timezone) : '—'}
                  />
                  {booking?.bookedCapacity > 1 && (
                    <DetailItem
                      icon="eva:people-outline"
                      label="Capacity"
                      value={`${booking.bookedCapacity} slots`}
                    />
                  )}
                </Box>

                {/* Created Date */}
                {booking?.createdAt && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1.5 }}>
                    Booked on {dayjs(booking.createdAt).format('MMM D, YYYY [at] h:mm A')}
                  </Typography>
                )}

                <Divider sx={{ my: 2.5 }} />

                {/* Customer Info Section */}
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Customer
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: 'background.neutral',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Iconify icon="eva:person-fill" width={20} height={20} sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2">
                      {booking?.customer
                        ? `${booking.customer.firstName} ${booking.customer.lastName}`
                        : '—'}
                    </Typography>
                  </Stack>

                  {(booking?.customer?.email || customerDetails?.email) && (
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                      <Iconify icon="eva:email-outline" width={16} height={16} sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {customerDetails?.email || booking.customer.email}
                      </Typography>
                    </Stack>
                  )}

                  {customerDetails?.phoneNumber && (
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                      <Iconify icon="eva:phone-outline" width={16} height={16} sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {customerDetails.phoneNumber}
                      </Typography>
                    </Stack>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      mt: 1,
                      display: 'inline-block',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => {
                      handleCloseModal();
                      navigate('/dashboard/customers');
                    }}
                  >
                    View Profile
                  </Typography>
                </Box>

                {/* Payment Details Section */}
                {user.config.enablePayments && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Payment
                    </Typography>
                    <Box
                      rowGap={2}
                      columnGap={2}
                      display="grid"
                      gridTemplateColumns={{
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                      }}
                    >
                      <DetailItem
                        icon="eva:pricetags-outline"
                        label="Price"
                        value={booking?.price
                          ? `${getSymbol(user.config.currency)} ${booking.price}`
                          : 'FREE'}
                      />
                      {booking?.paymentMode && (
                        <DetailItem
                          icon="eva:credit-card-outline"
                          label="Payment Mode"
                          value={sentenceCase(booking.paymentMode)}
                        />
                      )}
                      {booking?.paymentId && (
                        <DetailItem
                          icon="eva:hash-outline"
                          label="Payment ID"
                          value={booking.paymentId}
                        />
                      )}
                    </Box>
                  </>
                )}

                {/* Additional Info Section */}
                {(booking?.additionalData || booking?.internalNote) && (
                  <>
                    <Divider sx={{ my: 2.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      Notes
                    </Typography>

                    {booking?.additionalData && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Additional Data
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {booking.additionalData}
                        </Typography>
                      </Box>
                    )}

                    {booking?.internalNote && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Internal Note
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {booking.internalNote}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
            {!isNew && booking?._id && <NotificationHistory bookingId={booking._id} />}
          </Stack>

          {isNew && submitError && (
            <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* Bottom Buttons */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            flexGrow={1}
            sx={{ mr: 2, mb: 2 }}
          >
            {isNew ? (
              <>
                <Button
                  onClick={handleCloseModal}
                  size="medium"
                  color="inherit"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  size="medium"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Save
                </LoadingButton>
              </>
            ) : (
              <>
                <Button
                  onClick={() => generateIcsFile(booking)}
                  size="medium"
                  variant="outlined"
                  startIcon={<Iconify icon="eva:calendar-outline" />}
                >
                  Add to Calendar
                </Button>
                <Button
                  onClick={handleCloseModal}
                  size="medium"
                  color="inherit"
                  variant="outlined"
                >
                  Close
                </Button>
              </>
            )}
          </Stack>
        </FormProvider>
      </Scrollbar>
    </Drawer>

    {/* Unsaved Changes Dialog - only for new bookings */}
    {isNew && (
      <UnsavedChangesDialog
        open={unsavedChangesDialog.open}
        onClose={handleUnsavedChangesCancel}
        onDiscard={handleUnsavedChangesDiscard}
        isSaving={isSubmitting}
        title="Unsaved Booking Changes"
        message="You have unsaved changes to this booking. What would you like to do?"
      />
    )}
  </>
  );
}

function generateIcsFile(booking) {
  if (!booking?.startDate || !booking?.endDate) return;
  const start = dayjs(booking.startDate).utc().format('YYYYMMDDTHHmmss[Z]');
  const end = dayjs(booking.endDate).utc().format('YYYYMMDDTHHmmss[Z]');
  const summary = booking.service?.name || 'Booking';
  const customer = booking.customer
    ? `${booking.customer.firstName} ${booking.customer.lastName}`
    : '';
  const description = [
    customer ? `Customer: ${customer}` : '',
    booking.customer?.email ? `Email: ${booking.customer.email}` : '',
    booking.additionalData ? `Notes: ${booking.additionalData}` : '',
  ].filter(Boolean).join('\\n');

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FlowBookings//EN',
    'BEGIN:VEVENT',
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'STATUS:CONFIRMED',
    `UID:${booking._id}@flowbookings.io`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${summary.replace(/[^a-zA-Z0-9]/g, '-')}-booking.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function DetailItem({ icon, label, value }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <Iconify
        icon={icon}
        width={18}
        height={18}
        sx={{ color: 'text.secondary', mt: 0.2, flexShrink: 0 }}
      />
      <Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="body2">
          {value}
        </Typography>
      </Stack>
    </Stack>
  );
}

function ToolBar({ children }) {
  return (
    <Stack pt={3} pb={3} direction="row" alignItems="center">
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        flexGrow={1}
        pr={2}
      >
        {children}
      </Stack>
    </Stack>
  );
}
